import { Box } from '@mui/material';
import type { LevelTableColumn } from 'redesign';
import { TableCell, TableHeader } from 'redesign';
import { getExpenseWithNameAndValue } from 'shared/utils/expenses';
import { formatDuration, formatEmploymentType } from 'shared/utils/formatting';
import { Expense, WorkerEmploymentType } from '@types';
import { TFunctionNonStrict } from 'i18next';
import {
  ProjectsInboxCellProps,
  ProjectsInboxTableColumnDefinition,
  ProjectsInboxTableOptions,
} from '../components/projects-inbox-table.types';

export const getProjectsInboxColumnDefinition = (
  t: TFunctionNonStrict<'ogp-screen-specific', undefined>
): LevelTableColumn<ProjectsInboxTableColumnDefinition, ProjectsInboxTableOptions>[] => {
  const flexWorker: LevelTableColumn<
    ProjectsInboxTableColumnDefinition,
    ProjectsInboxTableOptions
  > = {
    accessor: 'flexWorker',
    Header: () => (
      <TableHeader>
        {t('checkouts.projects.table_headers.flexworker', { ns: 'ogp-screen-specific' })}
      </TableHeader>
    ),
    Cell: (cell) => <TableCell>{cell.value.fullName}</TableCell>,
  };
  const employmentType: LevelTableColumn<
    ProjectsInboxTableColumnDefinition,
    ProjectsInboxTableOptions
  > = {
    accessor: 'employmentType',
    Header: () => (
      <TableHeader>
        {t('checkouts.projects.table_headers.employment_type', { ns: 'ogp-screen-specific' })}
      </TableHeader>
    ),
    Cell: (cell) => (
      <TableCell> {formatEmploymentType(cell.value as WorkerEmploymentType)}</TableCell>
    ),
  };
  const project: LevelTableColumn<ProjectsInboxTableColumnDefinition, ProjectsInboxTableOptions> = {
    accessor: 'project',
    Header: () => (
      <TableHeader>
        {t('checkouts.projects.table_headers.project', { ns: 'ogp-screen-specific' })}
      </TableHeader>
    ),
    Cell: (cell) => <TableCell>{cell.value}</TableCell>,
  };
  const breakMinutes: LevelTableColumn<
    ProjectsInboxTableColumnDefinition,
    ProjectsInboxTableOptions
  > = {
    accessor: 'breakMinutes',
    Header: () => (
      <TableHeader>
        {t('checkouts.projects.table_headers.break', { ns: 'ogp-screen-specific' })}
      </TableHeader>
    ),
    Cell: (cell) => <TableCell>{formatDuration({ minutes: cell.value })}</TableCell>,
  };
  const workedFrom: LevelTableColumn<
    ProjectsInboxTableColumnDefinition,
    ProjectsInboxTableOptions
  > = {
    accessor: 'workedFrom',
    Header: () => (
      <TableHeader>
        {t('checkouts.projects.table_headers.worked_from', { ns: 'ogp-screen-specific' })}
      </TableHeader>
    ),
    Cell: (cell) => <TableCell>{cell.value}</TableCell>,
  };
  const workedTo: LevelTableColumn<ProjectsInboxTableColumnDefinition, ProjectsInboxTableOptions> =
    {
      accessor: 'workedTo',
      Header: () => (
        <TableHeader>
          {t('checkouts.projects.table_headers.worked_to', { ns: 'ogp-screen-specific' })}
        </TableHeader>
      ),
      Cell: (cell) => <TableCell>{cell.value}</TableCell>,
    };
  const actions: LevelTableColumn<ProjectsInboxTableColumnDefinition, ProjectsInboxTableOptions> = {
    accessor: 'actions',
    Header: '',
    Cell: ActionCell,
  };
  const expense: LevelTableColumn<ProjectsInboxTableColumnDefinition, ProjectsInboxTableOptions> = {
    Header: () => (
      <TableHeader>
        {t('checkouts.projects.table_headers.expenses', { ns: 'ogp-screen-specific' })}
      </TableHeader>
    ),
    accessor: 'expense',
    Cell: (cell) => (
      <TableCell>
        {cell.value?.length
          ? cell.value.map((expense: Expense) => (
              <Box key={expense.expenseType}>{getExpenseWithNameAndValue(expense)}</Box>
            ))
          : 'Geen'}
      </TableCell>
    ),
  };
  const branchOffice: LevelTableColumn<
    ProjectsInboxTableColumnDefinition,
    ProjectsInboxTableOptions
  > = {
    Header: () => (
      <TableHeader>
        {t('checkouts.projects.table_headers.branch_office', { ns: 'ogp-screen-specific' })}
      </TableHeader>
    ),
    accessor: 'branchOffice',
    Cell: (cell) => <TableCell>{cell.value}</TableCell>,
  };
  const department: LevelTableColumn<
    ProjectsInboxTableColumnDefinition,
    ProjectsInboxTableOptions
  > = {
    Header: () => (
      <TableHeader>
        {t('checkouts.projects.table_headers.department', { ns: 'ogp-screen-specific' })}
      </TableHeader>
    ),
    accessor: 'department',
    Cell: (cell) => <TableCell>{cell.value}</TableCell>,
  };

  return [
    flexWorker,
    project,
    branchOffice,
    department,
    employmentType,
    workedFrom,
    workedTo,
    expense,
    breakMinutes,
    actions,
  ];
};

const ActionCell = (cell: ProjectsInboxCellProps<'actions'>) => {
  const actions = cell.getRowActions(cell.value);
  return <>{actions}</>;
};
